/**
 * Binary data serialization utilities for API transfer
 * Uses base64 encoding for efficient binary data transfer
 */
import { bytesToBase64, base64ToBytes } from "@/utils/base64"

type SerializedBinary = {
	__binaryType__: "f32" | "u8"
	__binaryData__: string // base64 encoded
}

/**
 * Type guard to check if value is SerializedBinary
 */
function isSerializedBinary(value: unknown): value is SerializedBinary {
	if (value === null || typeof value !== "object") {
		return false
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const obj = value as Record<string, unknown>
	return (
		"__binaryType__" in obj &&
		"__binaryData__" in obj &&
		typeof obj.__binaryData__ === "string" &&
		(obj.__binaryType__ === "f32" || obj.__binaryType__ === "u8")
	)
}

/**
 * Recursively transform binary fields in an object for serialization
 * More efficient than JSON.stringify + JSON.parse for large binary data
 */
export function transformBinaryForSerialization(obj: unknown): unknown {
	if (obj === null || obj === undefined) {
		return obj
	}

	// Handle binary types
	if (obj instanceof Float32Array) {
		const uint8View = new Uint8Array(obj.buffer, obj.byteOffset, obj.byteLength)
		const result: SerializedBinary = {
			__binaryType__: "f32",
			__binaryData__: bytesToBase64(uint8View),
		}
		return result
	}

	if (obj instanceof Uint8Array) {
		const result: SerializedBinary = {
			__binaryType__: "u8",
			__binaryData__: bytesToBase64(obj),
		}
		return result
	}

	// Handle arrays
	if (Array.isArray(obj)) {
		return obj.map((item) => transformBinaryForSerialization(item))
	}

	// Handle plain objects
	if (typeof obj === "object" && obj.constructor === Object) {
		const result: Record<string, unknown> = {}
		for (const [key, value] of Object.entries(obj)) {
			result[key] = transformBinaryForSerialization(value)
		}
		return result
	}

	// Return primitives and other types as-is
	return obj
}

/**
 * Recursively restore binary fields from serialized format
 * More efficient than JSON.stringify + JSON.parse for large binary data
 */
export function transformBinaryFromSerialization(obj: unknown): unknown {
	if (obj === null || obj === undefined) {
		return obj
	}

	// Check if this is a serialized binary object
	if (isSerializedBinary(obj)) {
		const bytes = base64ToBytes(obj.__binaryData__)
		switch (obj.__binaryType__) {
			case "f32":
				return new Float32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 4)
			case "u8":
				return bytes
			default:
				return obj
		}
	}

	// Handle arrays
	if (Array.isArray(obj)) {
		return obj.map((item) => transformBinaryFromSerialization(item))
	}

	// Handle plain objects
	if (typeof obj === "object" && obj.constructor === Object) {
		const result: Record<string, unknown> = {}
		for (const [key, value] of Object.entries(obj)) {
			result[key] = transformBinaryFromSerialization(value)
		}
		return result
	}

	// Return primitives and other types as-is
	return obj
}
